import React from 'react';
import { Button, ButtonProps } from '@dropbox/dig-components/buttons';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styles from 'signer-app/signer-experience/next-button.module.css';
import { useHistory } from 'react-router';
import { useIsMutating } from '@tanstack/react-query';
import classNames from 'classnames';
import { Box } from '@dropbox/dig-foundations';
import { useAttachmentsInfo } from 'signer-app/utils/use-attachments-info';
import { Tooltip } from '@dropbox/dig-components/tooltips';
import { useFeatureFlag } from 'signer-app/context/feature-flags';

const messages = defineMessages({
  continueTooltip: {
    id: '651ba5d0685dc051e6f8ad3e70b62be43900d0f8fd36f213d112ce501906918d',
    description: 'Tooltip for the continue button',
    defaultMessage: 'Complete all required fields to continue',
  },
  continueTooltipWithAttachments: {
    id: '61f7e3306bb5dd3803d9acc89140891785bd1f0ef5f6ec3be1aa0a1d80e06db3',
    description: 'Tooltip for the continue button with attachments',
    defaultMessage:
      'Complete all required fields and add attachments to continue',
  },
});

export const ContinueButton: React.FC<{
  fullWidth?: boolean;
  size?: ButtonProps['size'];
  className?: string;
}> = ({ fullWidth, size, className }) => {
  const { step, signatureRequestId } = useSignerAppParams();
  const intl = useIntl();
  const {
    requiredFieldsLeft,
    startEnabled,
    allValid,
    isQualifiedSignature,
    flushDraft,
  } = useSignerContext();
  const [showTooltip, setShowTooltip] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const onMouseEnter = () => {
    setShowTooltip(true);
  };
  const onMouseLeave = () => {
    setShowTooltip(false);
  };

  const { requiredAttachmentsLeft } = useAttachmentsInfo();
  const isMutating = useIsMutating() > 0;
  const fixFinalizeRace = useFeatureFlag(
    'sign_core_2026_03_02_fix_finalize_saving_race',
  );
  const history = useHistory();

  const getBasePath = () => `/sign/${signatureRequestId}`;

  const getQesOrReviewPath = (currentStep: string = step) => {
    return isQualifiedSignature
      ? `${getBasePath()}/${currentStep}?qes`
      : `${getBasePath()}/review/`;
  };

  const handleContinue = () => {
    if (fixFinalizeRace) flushDraft();
    history.push(getQesOrReviewPath(step));
  };

  // If we're on the review step we should wait for any mutations to resolve
  // before allowing the user to click 'I Agree'
  const awaitingFinalSave = step === 'review' && isMutating;

  const disabled = React.useMemo(() => {
    if (!startEnabled) return true;

    if (awaitingFinalSave) return true;

    return !(
      requiredFieldsLeft === 0 &&
      allValid &&
      requiredAttachmentsLeft === 0
    );
  }, [
    requiredAttachmentsLeft,
    requiredFieldsLeft,
    allValid,
    startEnabled,
    awaitingFinalSave,
  ]);

  const shouldShowTooltip = disabled && showTooltip;
  const describedById = 'continue-button-tooltip';

  return (
    <>
      <Tooltip.Control
        id={describedById}
        open={shouldShowTooltip}
        placement="bottom"
        auto
        triggerRef={triggerRef}
      >
        {requiredAttachmentsLeft > 0
          ? intl.formatMessage(messages.continueTooltipWithAttachments)
          : intl.formatMessage(messages.continueTooltip)}
      </Tooltip.Control>
      <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <Button
          className={classNames(styles.nextButton, className)}
          variant={'primary'}
          fullWidth={fullWidth}
          size={size}
          disabled={disabled}
          onClick={handleContinue}
          data-testid="button-next"
          aria-describedby={shouldShowTooltip ? describedById : undefined}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          ref={triggerRef}
        >
          <FormattedMessage
            id="88555b111c0c2bcc4ac26c6cac92b51bc4ac747dff398cf96777fa2b10e11c48"
            defaultMessage="Continue"
            description="Next button in the signer experience."
          />
        </Button>
      </Box>
    </>
  );
};
